import React, { useState, useEffect } from "react";

import { useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { Box, useMediaQuery } from "@mui/material";

import { BackButton, Header } from "../../components";
import TicketHeader from "./components/TicketHeader";
import TicketSearchAndFilter from "./components/TicketSearchFilter";
import NewTicketForm from "./components/NewTicketForm";
import TicketList from "./components/TicketList";
import { getTickets, createTicket } from "services/clientService";
import { useLoader } from "context/loaderContext";
import { markAllTicketNoficationsAsRead } from "../../services/clientService";
import { useSocketContext } from "../../context/SocketContext";

const TicketDashboard = () => {
  const { showLoader, hideLoader } = useLoader();

  const [showCreateTicket, setShowCreateTicket] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [tickets, setTickets] = useState([]);

  const { unreadNotificationCount, setUnreadNotificationCount } =
    useSocketContext();

  useEffect(() => {
    const filtered = tickets.filter((ticket) => {
      // First check if the ticket matches the status filter
      const statusMatch =
        filterStatus === "All" || ticket.status === filterStatus;

      // Then check if the ticket matches the search term
      const searchMatch =
        searchTerm === "" ||
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.category.toLowerCase().includes(searchTerm.toLowerCase());

      // Return true only if both conditions are met
      return statusMatch && searchMatch;
    });

    // Update the filtered tickets state
    setFilteredTickets(filtered);
  }, [searchTerm, filterStatus, tickets]);

  const { palette, ...theme } = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getTicket = async () => {
    const res = await getTickets(showLoader, hideLoader);
    setTickets(res?.items || []);
  };

  useEffect(() => {
    getTicket();
  }, []);

  // When user reach this page, mark all notifications as read
  const markAllTicketNoficationsAsReadEx = async () => {
    await markAllTicketNoficationsAsRead();
  };

  useEffect(() => {
    if (unreadNotificationCount && unreadNotificationCount > 0) {
      markAllTicketNoficationsAsReadEx();
      setUnreadNotificationCount(0); // Reset unread notification count
    }
  }, [unreadNotificationCount]);

  // Toggle create ticket form
  const toggleCreateTicket = () => {
    setShowCreateTicket(!showCreateTicket);
  };

  return (
    <>
      <Header>
        <Box
          sx={{
            height: isMobile ? "calc(100vh - 76px)" : "calc(100vh - 170px)", // Account for header and footer
            overflow: "auto", // Enable scrolling
            display: "flex",
            flexDirection: "column",
            width: "100%",
            pt: isMobile ? 2 : 8, // Add top padding for spacing below header
            pb: 10, // Add bottom padding for spacing above footer
          }}
        >
          <Box
            sx={{
              width: "95px",
              height: "26px",
              position: "sticky",
              top: 0,
              left: isMobile ? "20px" : "111px",
              zIndex: 1,
              mb: 2,
            }}
          >
            {!isMobile && <BackButton onClick={() => navigate(-1)} />}
          </Box>

          <Box
            sx={{
              width: "95vw",
              maxWidth: "1620px", // Limit max width
              display: "flex",
              flexDirection: "column",
              gap: "32px",
              mx: "auto", // Center horizontally
              px: isMobile ? 2 : 4, // Add horizontal padding
              marginTop: "20px",
            }}
          >
            <TicketHeader onClickAddNew={toggleCreateTicket} />
            <TicketSearchAndFilter
              searchText={searchTerm}
              setSearchText={setSearchTerm}
              selectedFilter={filterStatus}
              setSelectedFilter={setFilterStatus}
            />
            {showCreateTicket && (
              <NewTicketForm
                onCancel={toggleCreateTicket}
                onSubmit={createTicket}
              />
            )}
            <TicketList filteredTickets={filteredTickets} />
          </Box>
        </Box>
      </Header>
    </>
  );
};

export default TicketDashboard;
